import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {Loaded} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {isLoaded as isLoadedPackages, PackageModel, UnassignedPackageModel} from '../PackageModel';

type AccidentNewYorkProps = {
  packageName: StringUnion;
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  replacement: boolean;
  section125: boolean;
  policyNumber: PolicyNumber;
  packageList: PackageModel;
  riderCodeList: I.List<string>;
  riderAmounts: I.List<number>;
  baseUnits: StringUnion;
  jStamp: boolean;
  situsState: StringUnion;
  plan: StringUnion;
};

export class InvalidGroupAccidentNewYork {
  public readonly key = 'InvalidGroupAccidentNewYork';

  constructor(
    public readonly packageName: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly replacement: boolean,
    public readonly section125: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly packageList: PackageModel,
    public readonly riderCodeList: I.List<string>,
    public readonly riderAmounts: I.List<number>,
    public readonly baseUnits: StringUnion,
    public readonly jStamp: boolean,
    public readonly situsState: StringUnion,
    public readonly plan: StringUnion
  ) {}
}

export class ValidGroupAccidentNewYork {
  public static createApp({
    packageName,
    planType,
    premium,
    calculatedPremium,
    cwa,
    replacement,
    section125,
    policyNumber,
    packageList,
    riderCodeList,
    riderAmounts,
    baseUnits,
    jStamp,
    situsState,
    plan,
  }: AccidentNewYorkProps) {
    return this.create(
      packageName,
      planType,
      premium,
      calculatedPremium,
      cwa,
      replacement,
      section125,
      policyNumber,
      packageList,
      riderCodeList,
      riderAmounts,
      baseUnits,
      jStamp,
      situsState,
      plan
    );
  }

  public static create(
    packageName: StringUnion,
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    replacement: boolean,
    section125: boolean,
    policyNumber: PolicyNumber,
    packageList: PackageModel,
    riderCodeList: I.List<string>,
    riderAmounts: I.List<number>,
    baseUnits: StringUnion,
    jStamp: boolean,
    situsState: StringUnion,
    plan: StringUnion
  ) {
    return isNonEmptyString(packageName) &&
      isNonEmptyString(planType) &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      riderCodeList.size > 0 &&
      riderAmounts.size > 0 &&
      section125 &&
      replacement &&
      policyNumber &&
      isLoadedPackages(packageList) &&
      isNonEmptyString(plan)
      ? new ValidGroupAccidentNewYork(
          packageName,
          planType,
          premium,
          calculatedPremium,
          cwa,
          replacement,
          section125,
          policyNumber,
          packageList,
          riderCodeList,
          riderAmounts,
          baseUnits,
          jStamp,
          situsState,
          plan
        )
      : new InvalidGroupAccidentNewYork(
          packageName,
          planType,
          premium,
          calculatedPremium,
          cwa,
          replacement,
          section125,
          policyNumber,
          packageList,
          riderCodeList,
          riderAmounts,
          baseUnits,
          jStamp,
          situsState,
          plan
        );
  }

  public readonly key = 'ValidGroupAccidentNewYork';
  constructor(
    public readonly packageName: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly replacement: boolean,
    public readonly section125: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly packageList: Loaded<object[]>,
    public readonly riderCodeList: I.List<string>,
    public readonly riderAmounts: I.List<number>,
    public readonly baseUnits: StringUnion,
    public readonly jStamp: boolean,
    public readonly situsState: StringUnion,
    public readonly plan: StringUnion
  ) {}
}

export type GroupAccidentNewYork = InvalidGroupAccidentNewYork | ValidGroupAccidentNewYork;

export type GroupAccidentNewYorkOptions<TReturn> = ToPartialMatchOptions<GroupAccidentNewYork, 'key', TReturn>;

export const matchGroupAccidentNewYork = <TReturn>(options: GroupAccidentNewYorkOptions<TReturn>) => (
  app: GroupAccidentNewYork
): TReturn => {
  switch (app.key) {
    case 'InvalidGroupAccidentNewYork':
      return options.InvalidGroupAccidentNewYork ? options.InvalidGroupAccidentNewYork(app) : options.default(app);
    case 'ValidGroupAccidentNewYork':
      return options.ValidGroupAccidentNewYork ? options.ValidGroupAccidentNewYork(app) : options.default(app);
  }
};

export const EmptyGroupAccidentNewYork = new InvalidGroupAccidentNewYork(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  EmptyPolicyNumber,
  new UnassignedPackageModel(),
  I.List<string>(),
  I.List<number>(),
  new EmptyString(),
  false,
  new EmptyString(),
  new EmptyString()
);

export const changePackage = (packageName: string) => (app: GroupAccidentNewYork) =>
  ValidGroupAccidentNewYork.createApp({...app, packageName: NonEmptyString.create(packageName)});

export const changePlanType = (planType: string) => (app: GroupAccidentNewYork) =>
  ValidGroupAccidentNewYork.createApp({...app, planType: NonEmptyString.create(planType)});

export const changePremium = (premium: string) => (app: GroupAccidentNewYork) =>
  ValidGroupAccidentNewYork.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: GroupAccidentNewYork) =>
  ValidGroupAccidentNewYork.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: GroupAccidentNewYork) =>
  ValidGroupAccidentNewYork.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeReplacement = (replacement: boolean) => (app: GroupAccidentNewYork) =>
  ValidGroupAccidentNewYork.createApp({...app, replacement, policyNumber: ValidPolicyNumber.create(new EmptyString())});

export const changeSection125 = (section125: boolean) => (app: GroupAccidentNewYork) =>
  ValidGroupAccidentNewYork.createApp({...app, section125});

export const changePolicyNumber = (policyNumber: StringUnion) => (app: GroupAccidentNewYork) =>
  ValidGroupAccidentNewYork.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changePackageList = (packages: PackageModel) => (app: GroupAccidentNewYork) =>
  ValidGroupAccidentNewYork.createApp({...app, packageList: packages});

export const changeBaseUnits = (baseUnits: string) => (app: GroupAccidentNewYork) =>
  ValidGroupAccidentNewYork.createApp({...app, baseUnits: NonEmptyString.create(baseUnits)});

export const changeJStamp = (jStamp: boolean) => (app: GroupAccidentNewYork) =>
  ValidGroupAccidentNewYork.createApp({...app, jStamp});

export const changeSitusState = (situsState: string) => (app: GroupAccidentNewYork) =>
  ValidGroupAccidentNewYork.createApp({...app, situsState: NonEmptyString.create(situsState)});

export const changePlan = (plan: string) => (app: GroupAccidentNewYork) =>
  ValidGroupAccidentNewYork.createApp({
    ...app,
    plan: NonEmptyString.create(plan),
    packageName: new EmptyString(),
    baseUnits: new EmptyString(),
  });

export const changeRiderCodes = (riderCode: string, index: number) => (app: GroupAccidentNewYork) => {
  const existingList: I.List<string> = app.riderCodeList;
  const newList = existingList.set(index, riderCode);

  return ValidGroupAccidentNewYork.createApp({...app, riderCodeList: newList});
};

export const changeRiderAmount = (riderAmt: number, index: number) => (app: GroupAccidentNewYork) => {
  const existingList: I.List<number> = app.riderAmounts;
  const newList = existingList.set(index, riderAmt);

  return ValidGroupAccidentNewYork.createApp({...app, riderAmounts: newList});
};

export type riderCodeType = {code: string; displayName: string; propertyName: string; units: number[]};

const riderCodes = [
  {
    code: '',
    displayName: '',
    propertyName: '',
    units: [],
  },
];

export const baseUnits = [1, 2, 3, 4, 5, 6];

export const plans = ['GVAP1NY'];

export const getRiderUnits = (riderCode: string | undefined) => {
  if (riderCode !== undefined && riderCode !== 'None') {
    const index = riderCodes.findIndex(rc => rc.code === riderCode);

    return riderCodes[index].units;
  }
  return [];
};

export const getRiderByPropertyName = (propertyName: string | undefined) => {
  if (propertyName !== undefined) {
    const index = riderCodes.findIndex(rc => rc.propertyName === propertyName);

    return riderCodes[index];
  }
  return {};
};

export const getAllRiderCodes = () => riderCodes;
